// src/pages/Keynotes.jsx
// Public keynotes / speaking page.
//
// Content is pulled from Firestore ("keynotes" collection, available == true)
// the same way Articles.jsx pulls books, so the admin panel can manage talks
// without a redeploy. FALLBACK_KEYNOTES below is only used if that collection
// is empty or unreachable, so the page never ships blank while it's being
// wired up — replace/remove it once the Firestore data is live.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import { fetchPublicContent } from "../data/publicContent";

import "./styles/keynote.css";

// ── Fallback content (used until Firestore "keynotes" is populated) ──────────

const FALLBACK_KEYNOTES = [
	{
		id: "7cs",
		eyebrow: "Signature keynote",
		title: "The 7Cs of High Performance",
		description:
			"A mindset-development perspective on becoming your own catalyst for high performance and consistent success, built around the 7 invisible Cs. Suited to people in demanding roles — leadership, entrepreneurship, high-pressure delivery — who want an exponential shift in results.",
		synopsisUrl: "",
		videoUrl: "",
		videoLabel: "Watch the 7Cs interview",
	},
	{
		id: "upward-mindset",
		eyebrow: "Culture & resilience",
		title: "Build an Upward Mindset",
		description:
			"A transformative keynote for teams navigating pressure and change — not just surviving it, but rising above it to build a culture of resilience, innovation, and collective success.",
		steps: ["Wake Up", "Rise Up", "Armour Up", "Keep Up", "Team Up"],
		synopsisUrl: "",
		videoUrl: "",
		videoLabel: "Watch highlights",
	},
];

// ── Small pieces ──────────────────────────────────────────────────────────────

// Extracts a YouTube video ID from common URL shapes, so we can pull a
// thumbnail image straight from YouTube. Returns null for anything that
// isn't a recognizable YouTube link (Vimeo, direct files, etc.) — those
// fall back to a generic play-button thumbnail.
function getYouTubeId(url) {
	try {
		const u = new URL(url);
		if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
		if (u.hostname.includes("youtube.com")) {
			if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2];
			return u.searchParams.get("v");
		}
	} catch {
		// not a valid absolute URL — treat as non-embeddable
	}
	return null;
}

// Small clickable thumbnail — shows the YouTube preview image with a play
// icon overlay. Clicking it opens the video straight on YouTube in a new
// tab; there's no in-page modal/preview anymore.
const VideoThumb = ({ videoUrl, videoLabel, title }) => {
	const youTubeId = getYouTubeId(videoUrl);
	const thumbSrc = youTubeId
		? `https://i.ytimg.com/vi/${youTubeId}/hqdefault.jpg`
		: null;

	return (
		<a
			className="kn-video-thumb"
			href={videoUrl}
			target="_blank"
			rel="noreferrer"
			aria-label={videoLabel || title}
		>
			<span className="kn-video-thumb__frame">
				{thumbSrc ? (
					<img
						src={thumbSrc}
						alt={`${title} — video preview`}
						className="kn-video-thumb__img"
						loading="lazy"
					/>
				) : (
					<span className="kn-video-thumb__img kn-video-thumb__img--placeholder" aria-hidden="true">
						🎬
					</span>
				)}
				<span className="kn-video-thumb__play" aria-hidden="true">
					<svg viewBox="0 0 24 24" width="16" height="16">
						<path d="M8 5v14l11-7z" fill="currentColor" />
					</svg>
				</span>
			</span>
			{videoLabel && <span className="kn-video-thumb__label">{videoLabel}</span>}
		</a>
	);
};

const StepStrip = ({ steps }) => (
	<ol className="kn-steps" aria-label="Keynote structure">
		{steps.map((step, i) => (
			<li key={step} className="kn-steps__item">
				<span className="kn-steps__index">{i + 1}</span>
				<span className="kn-steps__label">{step}</span>
			</li>
		))}
	</ol>
);

const KeynoteCard = ({ keynote, index }) => {
	const reversed = index % 2 === 1;

	return (
		<article
			className={`kn-row${reversed ? " kn-row--reverse" : ""}`}
			style={{ "--i": index }}
		>
			<div className="kn-row__media">
				<div className="kn-row__frame">
					{keynote.photoUrl ? (
						<img
							src={keynote.photoUrl}
							alt={`${keynote.title} — keynote by ${INFO.main.name || "the speaker"}`}
							className="kn-row__photo"
						/>
					) : (
						<div className="kn-row__photo kn-row__photo--placeholder" aria-hidden="true">
							<span>🎤</span>
						</div>
					)}
				</div>
				<div className="kn-row__badge">
					<span className="kn-row__badge-mark">“</span>
					<span className="kn-row__badge-text">{keynote.eyebrow}</span>
				</div>
			</div>

			<div className="kn-row__content">
				<div className="kn-eyebrow">{keynote.eyebrow}</div>
				<h2 className="kn-row__title">{keynote.title}</h2>
				<p className="kn-row__desc">{keynote.description}</p>

				{keynote.steps && keynote.steps.length > 0 && (
					<StepStrip steps={keynote.steps} />
				)}

				<div className="kn-row__actions">
					{keynote.synopsisUrl ? (
						<a
							className="kn-btn kn-btn--outline"
							href={keynote.synopsisUrl}
							target="_blank"
							rel="noreferrer"
						>
							Download keynote synopsis
						</a>
					) : (
						<span className="kn-btn kn-btn--outline kn-btn--disabled">
							Synopsis coming soon
						</span>
					)}

					{keynote.videoUrl ? (
						<VideoThumb
							videoUrl={keynote.videoUrl}
							videoLabel={keynote.videoLabel}
							title={keynote.title}
						/>
					) : null}
				</div>
			</div>
		</article>
	);
};

const KeynoteSkeleton = ({ index }) => (
	<div className={`kn-row kn-row--skeleton${index % 2 === 1 ? " kn-row--reverse" : ""}`}>
		<div className="kn-row__media">
			<div className="kn-row__frame kn-row__frame--skeleton" />
		</div>
		<div className="kn-row__content">
			<div className="kn-skel kn-skel--eyebrow" />
			<div className="kn-skel kn-skel--title" />
			<div className="kn-skel kn-skel--line" />
			<div className="kn-skel kn-skel--line" style={{ width: "80%" }} />
		</div>
	</div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const Keynotes = () => {
	const [keynotes, setKeynotes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetchPublicContent("keynotes")
			.then((docs) => {
				if (cancelled) return;
				docs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
				setKeynotes(docs);
			})
			.catch((err) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => { cancelled = true; };
	}, []);

	const displayKeynotes = !loading && keynotes.length === 0 ? FALLBACK_KEYNOTES : keynotes;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Keynotes | ${INFO.main.title}`}</title>
			</Helmet>

			<div className="page-content">
				<NavBar active="keynotes" />
				<div className="content-wrapper">
					<div className="articles-logo-container">
						<div className="articles-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="kn-main-container">
						{/* Hero */}
						<header className="kn-hero">
							<div className="kn-eyebrow kn-eyebrow--hero">Speaking &amp; facilitation</div>
							<h1 className="kn-hero__title">Keynotes that move a room</h1>
							<p className="kn-hero__subtitle">
								Talks built on lived experience and practical frameworks — for
								leadership offsites, conferences, and teams working through change.
							</p>
						</header>

						{/* Keynotes */}
						<div className="kn-list">
							{loading &&
								[0, 1].map((i) => <KeynoteSkeleton key={i} index={i} />)}

							{error && (
								<div className="kn-state kn-state--error">
									Could not load keynotes right now. Please try again later.
								</div>
							)}

							{!loading &&
								!error &&
								displayKeynotes.map((keynote, i) => (
									<KeynoteCard key={keynote.id} keynote={keynote} index={i} />
								))}
						</div>

						{/* Booking CTA */}
						<div className="kn-cta">
							<div className="kn-cta__text">
								<h3>Ready to bring one of these to your stage?</h3>
								<p>Check available dates and send a booking request in a few clicks.</p>
							</div>
							{/* Swap for <Link to="/booking"> if this project uses react-router's Link elsewhere */}
							<a className="kn-btn kn-btn--primary" href="/contact" target="_blank" rel="noreferrer">
								Book a keynote
							</a>
						</div>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Keynotes;